import { useState, useEffect } from 'react'
import { useI18n } from '@/lib/i18n'
import type { EventConfig } from '@/types'
import { CAPTURE_MODES } from '@/lib/constants'
import { Camera, Clapperboard, Film, RotateCcw } from 'lucide-react'

const MODE_META = (t: any): Record<string, { icon: React.ElementType; color: string; description: string }> => ({
  photo: { icon: Camera, color: 'border-primary/40 bg-primary/10 text-primary', description: t('capture.photoDesc') },
  gif: { icon: Film, color: 'border-border bg-secondary text-muted-foreground', description: t('capture.gifDesc') },
  boomerang: { icon: RotateCcw, color: 'border-amber-500/40 bg-amber-500/10 text-amber-400', description: t('capture.boomerangDesc') },
  video: { icon: Clapperboard, color: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-400', description: t('capture.videoDesc') },
})

function SectionHeader({ title, description }: { title: string; description?: string }) {
  return (
    <div className="mb-4">
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      {description && <p className="text-xs text-muted-foreground mt-0.5">{description}</p>}
    </div>
  )
}

function SliderRow({ label, value, min, max, step = 1, unit = '', onChange }: {
  label: string; value: number; min: number; max: number; step?: number; unit?: string; onChange: (v: number) => void
}) {
  return (
    <div className="flex items-center gap-4">
      <span className="w-44 shrink-0 text-sm text-muted-foreground">{label}</span>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} />
      <span className="w-14 shrink-0 rounded-md bg-muted px-2 py-0.5 text-center text-sm font-mono text-foreground">{value}{unit}</span>
    </div>
  )
}

export default function CaptureSettings({ event, onChange }: { event: EventConfig; onChange: (u: Partial<EventConfig>) => void }) {
  const { t } = useI18n()
  const { capture } = event
  const [testStatus, setTestStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle')
  const [testError, setTestError] = useState<string | null>(null)
  const [ws, setWs] = useState<WebSocket | null>(null)
  const [lightState, setLightState] = useState<'ON' | 'OFF'>('OFF')

  useEffect(() => {
    return () => {
      if (ws) ws.close()
    }
  }, [ws])

  const toggleMode = (mode: typeof CAPTURE_MODES[number]) => {
    const enabled = capture.enabledModes.includes(mode)
      ? capture.enabledModes.filter((m) => m !== mode)
      : [...capture.enabledModes, mode]
    if (enabled.length === 0) return // at least one mode required
    onChange({ capture: { ...capture, enabledModes: enabled as typeof capture.enabledModes } })
  }

  const updatePhoto = (key: keyof typeof capture.photo, value: number | boolean) => {
    onChange({ capture: { ...capture, photo: { ...capture.photo, [key]: value } } })
  }

  return (
    <div className="space-y-8 max-w-2xl">
      <div>
        <h2 className="text-xl font-bold">{t('editor.captureSettings')}</h2>
        <p className="text-sm text-muted-foreground mt-1">{t('capture.photoSettingsDesc')}</p>
      </div>

      {/* Capture mode cards */}
      <div>
        <div className="section-header">
          <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{t('capture.modes')}</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {CAPTURE_MODES.map((mode) => {
            const meta = MODE_META(t)[mode]
            const Icon = meta?.icon ?? Camera
            const active = capture.enabledModes.includes(mode)
            const isDisabled = mode !== 'photo'
            return (
              <button
                key={mode}
                onClick={() => {
                  if (isDisabled) {
                    alert('Coming soon!')
                    return
                  }
                  toggleMode(mode)
                }}
                className={`relative flex items-start gap-3 rounded-xl border p-4 text-left transition-all ${
                  isDisabled
                    ? 'border-dashed border-border/50 bg-card/30 text-muted-foreground/50 cursor-not-allowed'
                    : active
                      ? meta?.color ?? 'border-primary/40 bg-primary/10 text-primary'
                      : 'border-border bg-card text-muted-foreground hover:border-border/80 hover:bg-muted'
                }`}
              >
                <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${isDisabled ? 'bg-muted/50' : active ? 'bg-white/10' : 'bg-muted'}`}>
                  <Icon className="h-4 w-4" />
                </div>
                <div>
                  <div className="font-semibold capitalize text-sm">{t(`capture.${mode}`)}</div>
                  <div className="text-xs opacity-70 mt-0.5">{t(`capture.${mode}Desc`)}</div>
                </div>
                {isDisabled && (
                  <div className="absolute top-3 right-3 text-[10px] font-bold uppercase text-muted-foreground/60 bg-muted/80 px-1.5 py-0.5 rounded">Coming Soon</div>
                )}
                {active && !isDisabled && (
                  <div className="absolute top-3 right-3 h-2 w-2 rounded-full bg-current opacity-80" />
                )}
              </button>
            )
          })}
        </div>
      </div>

      {/* Photo settings */}
      {capture.enabledModes.includes('photo') && (
        <div className="rounded-xl border border-border bg-card/50 p-5 space-y-4">
          <SectionHeader title={t('capture.photoSettings')} description={t('capture.photoSettingsDesc')} />

          <SliderRow label={t('capture.delayBeforeFirst')} value={capture.photo.delayBeforeFirst} min={1} max={20} onChange={(v) => updatePhoto('delayBeforeFirst', v)} unit="s" />
          <SliderRow label={t('capture.delayBetweenPoses')} value={capture.photo.delayBetweenPoses} min={1} max={20} onChange={(v) => updatePhoto('delayBetweenPoses', v)} unit="s" />
          <SliderRow label={t('capture.reviewTime')} value={capture.photo.reviewTime} min={1} max={60} onChange={(v) => updatePhoto('reviewTime', v)} unit="s" />
          <SliderRow 
            label={t('capture.poseIntroDuration')} 
            value={capture.poseSuggestions?.showSecondsBefore ?? 2} 
            min={0} 
            max={20} 
            onChange={(v) => onChange({ 
              capture: { 
                ...capture, 
                poseSuggestions: { 
                  enabled: capture.poseSuggestions?.enabled ?? true,
                  showSecondsBefore: v 
                } 
              } 
            })} 
            unit="s" 
          />
          
          <div className="border-t border-border/50 pt-4 mt-4 space-y-4">
            <label className="flex items-center gap-3 rounded-lg border border-border bg-background px-4 py-3 cursor-pointer hover:bg-muted transition-colors">
              <input 
                type="checkbox" 
                checked={capture.poseSuggestions?.enabled ?? true} 
                onChange={(e) => onChange({ 
                  capture: { 
                    ...capture, 
                    poseSuggestions: { 
                      enabled: e.target.checked,
                      showSecondsBefore: capture.poseSuggestions?.showSecondsBefore ?? 2
                    } 
                  } 
                })} 
              />
              <div>
                <div className="text-sm font-medium">{t('capture.enablePoseSuggestions')}</div>
                <div className="text-xs text-muted-foreground">{t('capture.poseSuggestionsSub')}</div>
              </div>
            </label>
          </div>

          <label className="flex items-center gap-3 rounded-lg border border-border bg-background px-4 py-3 cursor-pointer hover:bg-muted transition-colors">
            <input type="checkbox" checked={capture.photo.alsoCreateGif} onChange={(e) => updatePhoto('alsoCreateGif', e.target.checked)} />
            <div>
              <div className="text-sm font-medium">{t('capture.alsoCreateGif')}</div>
              <div className="text-xs text-muted-foreground">{t('capture.gifDesc')}</div>
            </div>
          </label>
        </div>
      )}

      {/* GIF settings */}
      {capture.enabledModes.includes('gif') && (
        <div className="rounded-xl border border-border bg-card/50 p-5 space-y-4">
          <SectionHeader title={t('capture.gifSettings')} description={t('capture.gifSettingsDesc')} />
          <SliderRow label={t('capture.frameCount')} value={capture.gif.frameCount} min={3} max={30} onChange={(v) => onChange({ capture: { ...capture, gif: { ...capture.gif, frameCount: v } } })} unit={` ${t('capture.frameCount').toLowerCase()}s`} />
          <SliderRow label={t('capture.frameDelay')} value={capture.gif.frameDelay} min={50} max={1000} step={50} onChange={(v) => onChange({ capture: { ...capture, gif: { ...capture.gif, frameDelay: v } } })} unit="ms" />
        </div>
      )}

      {/* Boomerang settings */}
      {capture.enabledModes.includes('boomerang') && (
        <div className="rounded-xl border border-border bg-card/50 p-5 space-y-4">
          <SectionHeader title={t('capture.boomerangSettings')} description={t('capture.boomerangSettingsDesc')} />
          <SliderRow label={t('capture.duration')} value={capture.boomerang.duration} min={1} max={10} onChange={(v) => onChange({ capture: { ...capture, boomerang: { ...capture.boomerang, duration: v } } })} unit="s" />
          <SliderRow label={t('capture.speed')} value={capture.boomerang.speed} min={0.5} max={3} step={0.1} onChange={(v) => onChange({ capture: { ...capture, boomerang: { ...capture.boomerang, speed: v } } })} />
        </div>
      )}

      {/* Video settings */}
      {capture.enabledModes.includes('video') && (
        <div className="rounded-xl border border-border bg-card/50 p-5 space-y-4">
          <SectionHeader title={t('capture.videoSettings')} description={t('capture.videoSettingsDesc')} />
          <SliderRow label={t('capture.maxDuration')} value={capture.video.maxDuration} min={5} max={120} onChange={(v) => onChange({ capture: { ...capture, video: { ...capture.video, maxDuration: v } } })} unit="s" />
        </div>
      )}

      {/* Global session settings */}
      <div className="rounded-xl border border-border bg-card/50 p-5 space-y-4">
        <SectionHeader title={t('capture.sessionSettings')} description={t('capture.sessionSettingsDesc')} />
        <SliderRow label={t('capture.finishScreenTimeout')} value={capture.finishScreenTimeout || 20} min={5} max={120} onChange={(v) => onChange({ capture: { ...capture, finishScreenTimeout: v } })} unit="s" />
        <label className="flex items-center gap-3 rounded-lg border border-border bg-background px-4 py-3 cursor-pointer hover:bg-muted transition-colors">
          <input type="checkbox" checked={capture.recordSessionVideo || false} onChange={(e) => onChange({ capture: { ...capture, recordSessionVideo: e.target.checked } })} />
          <div>
            <div className="text-sm font-medium">{t('capture.recordSessionVideo')}</div>
            <div className="text-xs text-muted-foreground">{t('capture.recordSessionVideoDesc')}</div>
          </div>
        </label>
      </div>
      {/* Data Saver Mode */}
      <div className="rounded-xl border border-border bg-card/50 p-5 space-y-4">
        <SectionHeader title={t('capture.dataSaver')} description={t('capture.dataSaverDesc')} />
        <label className="flex items-center gap-3 rounded-lg border border-border bg-background px-4 py-3 cursor-pointer hover:bg-muted transition-colors">
          <input
            type="checkbox"
            checked={capture.dataSaver?.enabled ?? false}
            onChange={(e) => onChange({
              capture: {
                ...capture,
                dataSaver: {
                  enabled: e.target.checked,
                  quality: capture.dataSaver?.quality ?? 0.75,
                  maxDimension: capture.dataSaver?.maxDimension ?? 1600,
                },
              },
            })}
          />
          <div>
            <div className="text-sm font-medium">{t('capture.dataSaverEnable')}</div>
            <div className="text-xs text-muted-foreground">{t('capture.dataSaverEnableSub')}</div>
          </div>
        </label>

        {capture.dataSaver?.enabled && (
          <div className="space-y-4 pt-1">
            <SliderRow
              label={t('capture.dataSaverQuality')}
              value={Math.round((capture.dataSaver?.quality ?? 0.75) * 100)}
              min={40}
              max={95}
              step={5}
              unit="%"
              onChange={(v) => onChange({
                capture: {
                  ...capture,
                  dataSaver: {
                    enabled: true,
                    quality: v / 100,
                    maxDimension: capture.dataSaver?.maxDimension ?? 1600,
                  },
                },
              })}
            />
            <SliderRow
              label={t('capture.dataSaverMaxDimension')}
              value={capture.dataSaver?.maxDimension ?? 1600}
              min={800}
              max={2560}
              step={80}
              unit="px"
              onChange={(v) => onChange({
                capture: {
                  ...capture,
                  dataSaver: {
                    enabled: true,
                    quality: capture.dataSaver?.quality ?? 0.75,
                    maxDimension: v,
                  },
                },
              })}
            />
            <p className="text-xs text-muted-foreground">{t('capture.dataSaverNote')}</p>
          </div>
        )}
      </div>

      {/* Zigbee Light */}
      <div className="rounded-xl border border-border/50 bg-card p-6 space-y-4 mt-6">
        <SectionHeader title="Zigbee Photo Light" description="Configure a local Zigbee light to turn on during photo capture." />
        
        <label className="flex items-center gap-3 rounded-lg border border-border bg-background px-4 py-3 cursor-pointer hover:bg-muted transition-colors">
          <input 
            type="checkbox" 
            checked={capture.zigbee?.enabled || false} 
            onChange={(e) => onChange({ 
              capture: { 
                ...capture, 
                zigbee: { 
                  ...(capture.zigbee || { device: 'spotlight', wsUrl: 'ws://localhost:8081/api' }), 
                  enabled: e.target.checked 
                } 
              } 
            })} 
          />
          <div>
            <div className="text-sm font-medium">Enable Photo Light</div>
            <div className="text-xs text-muted-foreground">Turn on the light for 3 seconds when taking a photo.</div>
          </div>
        </label>

        <div className="flex items-center gap-4">
          <span className="w-44 shrink-0 text-sm text-muted-foreground">Mode</span>
          <select 
            value={capture.zigbee?.mode || 'auto'} 
            onChange={(e) => onChange({ 
              capture: { 
                ...capture, 
                zigbee: { 
                  ...(capture.zigbee || { enabled: false, device: 'spotlight', wsUrl: 'ws://localhost:8081/api' }), 
                  mode: e.target.value as 'off' | 'always' | 'auto' | 'session'
                } 
              } 
            })} 
            className="w-full max-w-sm rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:border-primary"
          >
            <option value="off">Off</option>
            <option value="auto">Automatic (During Countdown)</option>
            <option value="session">Always On (During Session)</option>
            <option value="always">Always On (Continuous)</option>
          </select>
        </div>

        {(capture.zigbee?.mode === 'auto' || !capture.zigbee?.mode) && (
          <>
            <div className="flex items-center gap-4">
              <span className="w-44 shrink-0 text-sm text-muted-foreground">On Before Photo (s)</span>
              <input 
                type="number" 
                value={capture.zigbee?.onBeforeSeconds || 3} 
                onChange={(e) => onChange({ 
                  capture: { 
                    ...capture, 
                    zigbee: { 
                      ...(capture.zigbee || { enabled: false, device: 'spotlight', wsUrl: 'ws://localhost:8081/api' }), 
                      onBeforeSeconds: Number(e.target.value)
                    } 
                  } 
                })} 
                className="w-full max-w-sm rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:border-primary"
                min={0}
                max={10}
              />
            </div>

            <div className="flex items-center gap-4">
              <span className="w-44 shrink-0 text-sm text-muted-foreground">Off After Photo (s)</span>
              <input 
                type="number" 
                value={capture.zigbee?.offAfterSeconds || 2} 
                onChange={(e) => onChange({ 
                  capture: { 
                    ...capture, 
                    zigbee: { 
                      ...(capture.zigbee || { enabled: false, device: 'spotlight', wsUrl: 'ws://localhost:8081/api' }), 
                      offAfterSeconds: Number(e.target.value)
                    } 
                  } 
                })} 
                className="w-full max-w-sm rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:border-primary"
                min={0}
                max={10}
              />
            </div>
          </>
        )}

        <div className="flex items-center gap-4">
          <span className="w-44 shrink-0 text-sm text-muted-foreground">Device Name</span>
          <input 
            type="text" 
            value={capture.zigbee?.device || 'spotlight'} 
            onChange={(e) => onChange({ 
              capture: { 
                ...capture, 
                zigbee: { 
                  ...(capture.zigbee || { enabled: false, wsUrl: 'ws://localhost:8081/api' }), 
                  device: e.target.value 
                } 
              } 
            })} 
            className="w-full max-w-sm rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:border-primary"
            placeholder="e.g. spotlight"
          />
        </div>

        <div className="flex items-center gap-4">
          <span className="w-44 shrink-0 text-sm text-muted-foreground">WS URL</span>
          <input 
            type="text" 
            value={capture.zigbee?.wsUrl || 'ws://localhost:8081/api'} 
            onChange={(e) => onChange({ 
              capture: { 
                ...capture, 
                zigbee: { 
                  ...(capture.zigbee || { enabled: false, device: 'spotlight' }), 
                  wsUrl: e.target.value 
                } 
              } 
            })} 
            className="w-full max-w-sm rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:border-primary"
            placeholder="ws://localhost:8081/api"
          />
        </div>

        <div className="flex items-center gap-4">
          <span className="w-44 shrink-0 text-sm text-muted-foreground">Base Topic</span>
          <input 
            type="text" 
            value={capture.zigbee?.baseTopic || 'zigbee2mqtt'} 
            onChange={(e) => onChange({ 
              capture: { 
                ...capture, 
                zigbee: { 
                  ...(capture.zigbee || { enabled: false, device: 'spotlight', wsUrl: 'ws://localhost:8081/api' }), 
                  baseTopic: e.target.value 
                } 
              } 
            })} 
            className="w-full max-w-sm rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:border-primary"
            placeholder="e.g. zigbee2mqtt"
          />
        </div>

        <div className="flex items-center gap-4 pt-2">
          <span className="w-44 shrink-0 text-sm text-muted-foreground">Connection Test</span>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => {
                setTestStatus('testing')
                setTestError(null)
                
                const url = capture.zigbee?.wsUrl || 'ws://localhost:8081/api'
                console.log('Testing connection to:', url)
                const socket = new WebSocket(url)
                
                socket.onopen = () => {
                  console.log('Test connection successful')
                  setTestStatus('success')
                  setWs(socket)
                }
                
                socket.onerror = (err) => {
                  console.error('Test connection error:', err)
                  setTestStatus('error')
                  setTestError('Failed to connect')
                  socket.close()
                }
                
                socket.onclose = () => {
                  console.log('Test connection closed')
                  setWs(null)
                  // Don't overwrite error status if it failed
                }
              }}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                testStatus === 'testing' ? 'bg-muted text-muted-foreground cursor-wait' :
                testStatus === 'success' ? 'bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20' :
                testStatus === 'error' ? 'bg-red-500/10 text-red-500 hover:bg-red-500/20' :
                'bg-primary/10 text-primary hover:bg-primary/20'
              }`}
              disabled={testStatus === 'testing'}
            >
              {testStatus === 'testing' ? 'Connecting...' :
               testStatus === 'success' ? 'Connected' :
               testStatus === 'error' ? 'Retry Test' :
               'Test Connection'}
            </button>

            {testStatus === 'success' && ws && (
              <div className="flex items-center gap-2 border-l border-border/50 pl-3">
                <span className="text-xs text-muted-foreground">Light:</span>
                <div className="flex items-center gap-1 bg-muted rounded-lg p-0.5">
                  <button
                    type="button"
                    onClick={() => {
                      const device = capture.zigbee?.device || 'spotlight'
                      ws.send(JSON.stringify({ topic: `${device}/set/state`, payload: 'OFF' }))
                      setLightState('OFF')
                    }}
                    className={`px-2 py-0.5 text-xs font-medium rounded-md transition-colors ${
                      lightState === 'OFF' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    OFF
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const device = capture.zigbee?.device || 'spotlight'
                      ws.send(JSON.stringify({ topic: `${device}/set/state`, payload: 'ON' }))
                      setLightState('ON')
                    }}
                    className={`px-2 py-0.5 text-xs font-medium rounded-md transition-colors ${
                      lightState === 'ON' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    ON
                  </button>
                </div>
              </div>
            )}

            {testStatus === 'error' && (
              <span className="text-xs text-red-500">{testError}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
